import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  createHashRouter,
  createRoutesFromElements,
  Navigate,
  Route,
  RouterProvider,
} from 'react-router'

import { isApiError } from '@/shared/api'
import { LocalizationProvider, systemTranslate, useTranslate } from '@/shared/i18n'
import { TelegramProvider } from '@/shared/telegram'
import type { TelegramSession, TelegramStatus } from '@/shared/telegram'
import { Screen, ToastProvider } from '@/shared/ui'

import { ApiProvider } from './api'
import { Layout } from './Layout'
import { routes, routeOfStartParam } from './routes'
import './app.css'

import { ChatPage } from '@/pages/chat/ChatPage'
import { AreaPage } from '@/pages/areas/AreaPage'
import { DashboardPage } from '@/pages/dashboard/DashboardPage'
import { DocumentPage } from '@/pages/documents/DocumentPage'
import { DocumentsPage } from '@/pages/documents/DocumentsPage'
import { OnboardingPage } from '@/pages/onboarding/OnboardingPage'
import { TimelinePage } from '@/pages/timeline/TimelinePage'
import { SettingsPage } from '@/pages/settings/SettingsPage'
import { SurveyPage } from '@/pages/survey/SurveyPage'
import { SubscriptionPage } from '@/pages/subscription/SubscriptionPage'
import { TaskPage } from '@/pages/tasks/TaskPage'
import { TasksPage } from '@/pages/tasks/TasksPage'

function Bootstrapping({
  status,
  reason,
}: {
  status: Exclude<TelegramStatus, 'ready'>
  reason?: string | undefined
}) {
  if (status === 'starting') {
    return <Screen title={systemTranslate('app.loading')} />
  }

  const prefix = status === 'outside' ? 'telegram.outside' : 'telegram.failed'

  return (
    <Screen title={systemTranslate(`${prefix}.title`)} body={systemTranslate(`${prefix}.body`)}>
      {reason ? <p className="screen__reason">{reason}</p> : null}
    </Screen>
  )
}

function Reauthorize() {
  const t = useTranslate()

  return (
    <Screen
      title={t('error.unauthorized.title')}
      body={t('error.unauthorized.body')}
      action={{ label: t('app.retry'), onClick: () => window.location.reload() }}
    />
  )
}

function newQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        retry: (attempt, error) => attempt < 2 && !isApiError(error),
      },
    },
  })
}

// A data router rather than <HashRouter>: view transitions, and the
// useViewTransitionState hook the attention cards read to morph into the screen
// they open, exist only on this side of react-router. On a non-data router the
// hook throws and takes the whole screen down with it.
//
// Exported so a test can mount the same routes through createMemoryRouter —
// nothing else proves the tree actually renders.
export function appRoutes(startParam: string | undefined) {
  const deepLink = new DeepLink(routeOfStartParam(startParam))

  return createRoutesFromElements(
    <Route>
      <Route path={routes.onboarding} element={<OnboardingPage />} />
      <Route path={routes.survey} element={<SurveyPage />} />
      <Route element={<Layout />}>
        <Route path={routes.dashboard} element={<Home deepLink={deepLink} />} />
        <Route path={routes.documents} element={<DocumentsPage />} />
        <Route path={routes.document} element={<DocumentPage />} />
        <Route path={routes.timeline} element={<TimelinePage />} />
        <Route path={routes.tasks} element={<TasksPage />} />
        <Route path={routes.task} element={<TaskPage />} />
        <Route path={routes.area} element={<AreaPage />} />
        <Route path={routes.settings} element={<SettingsPage />} />
        <Route path={routes.subscription} element={<SubscriptionPage />} />
        <Route path={routes.chat} element={<ChatPage />} />
      </Route>
      <Route path="*" element={<Navigate to={routes.dashboard} replace />} />
    </Route>,
  )
}

class DeepLink {
  private target: string | undefined

  constructor(target: string | undefined) {
    this.target = target
  }

  pending(): string | undefined {
    return this.target
  }

  consume(): void {
    this.target = undefined
  }
}

function Home({ deepLink }: { deepLink: DeepLink }) {
  const [target] = useState(() => deepLink.pending())

  useEffect(() => deepLink.consume(), [deepLink])

  return target ? <Navigate to={target} replace /> : <DashboardPage />
}

function Session({ session }: { session: TelegramSession }) {
  const [expired, setExpired] = useState(false)
  const onUnauthorized = useCallback(() => setExpired(true), [])
  const client = useMemo(() => newQueryClient(), [])
  const router = useMemo(
    () => createHashRouter(appRoutes(session.startParam)),
    [session.startParam],
  )

  return (
    <LocalizationProvider hint={session.languageCode}>
      <QueryClientProvider client={client}>
        <ApiProvider session={session} onUnauthorized={onUnauthorized}>
          <ToastProvider>
            {expired ? <Reauthorize /> : <RouterProvider router={router} />}
          </ToastProvider>
        </ApiProvider>
      </QueryClientProvider>
    </LocalizationProvider>
  )
}

export function App() {
  return (
    <TelegramProvider
      fallback={(status, reason) => <Bootstrapping status={status} reason={reason} />}
    >
      {(session) => <Session session={session} />}
    </TelegramProvider>
  )
}
